import styled, { css } from 'styled-components';
import { zIndex } from 'styles';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import { DEEP_BORDER_BOX } from 'common';
import badgeStyled from 'components/Badge/styles';

export const NAVIGATION_DRAWER_WIDTH = 250;
export const COMPACT_NAVIGATION_DRAWER_WIDTH = 64;
export const COMPACT_ICON_SIZE = '1.5rem';

const {
  SPACING,
  SHADOW_SPREAD,
  FONT_WEIGHT,
  TYPOGRAPHY,
  COLORS,
} = Tokens;
const VERTICAL_BAR_WIDTH = SPACING.SPACING_4;
const VERTICAL_BAR_FOCUS_WIDTH = SPACING.SPACING_2;
const SPACING_20 = '1.25rem';
const SPACING_28 = '1.75rem';


const ITEM_HEIGHT = '4rem';
const SLIM_ITEM_HEIGHT = '2rem';
const BUTTON_OFFSET = `${parseFloat(SPACING.SPACING_24) - parseFloat(SPACING.SPACING_16)}`; // (SPACING.SPACING_24 - SPACING.SPACING_16, to offset AppBanner's padding)

const SELECTED_STYLE = theme => css`
  ${TYPOGRAPHY.BODY}
  font-weight: ${FONT_WEIGHT.FONT_WEIGHT_BOLD}; 
  pointer-events: auto;
  ${theme && `color: ${theme.sidedrawer.selected};`}
`;

const SELECTED_LABEL_STYLE = theme => css`
  font-weight: ${FONT_WEIGHT.FONT_WEIGHT_BOLD};
  ${theme && `color: ${theme.sidedrawer.selected};`}
`;

export const SideDrawerVerticalBar = styled.div`
  position: absolute;
  left: 0;
  height: 100%;
  width: ${VERTICAL_BAR_WIDTH};
  min-width: ${VERTICAL_BAR_WIDTH};
  background: ${COLORS.TRANSPARENT};
  ${({ active }) => active && css`
    background: ${props => props.$theme.sidedrawer.activeVerticalbar}
  `};
  ${({ selected }) => selected && css`
    background: ${props => props.$theme.sidedrawer.selectedVerticalBar}
  `};
  ${({ RTL }) => RTL && css`
    right: 0;
  `};
`;

export default {
  SideDrawerList: styled.div`
    ${DEEP_BORDER_BOX}
    ${({ RTL }) => (RTL ? 'right: 0' : 'left: 0')};
    width: 100%;
    height: 100%;
    overflow-x: visible;
    overflow-y: hidden;
    ${({ $isCompactAndClosed }) => $isCompactAndClosed && css`
      overflow-x: hidden;
    `};
  `,
  CompactBadgeWrapper: styled.span`
    position: absolute;
    right: 1.375rem;
    top: 1rem;
    z-index: ${zIndex.BADGE};
    background: transparent;
    height: ${SPACING.SPACING_12};
    width: ${SPACING.SPACING_12};
    border-radius: ${Tokens.RADIUS.RADIUS_CIRCLE};
    ${({ RTL }) => RTL && css`
      right: 0.4375rem;
    `};
    ${badgeStyled.Badge} {
      box-sizing: content-box;
    }
  `,
  SideDrawerItem: styled.div`  
    position: relative;
    box-sizing: border-box;
    cursor: pointer;
    width: 100%;
    ${({ RTL, groupItem }) => (RTL ? `padding-right: ${groupItem ? SPACING_20 : SPACING.SPACING_8};` : `padding-left: ${groupItem ? SPACING_20 : SPACING.SPACING_8};`)};
    ${({ groupItem, groupHeaderHasIcon }) => (groupItem && groupHeaderHasIcon) && css`
      ${({ RTL }) => (RTL ? 'padding-left: 0' : `padding-left: ${SPACING.SPACING_48}`)};
      ${({ RTL }) => (RTL ? `padding-right: ${SPACING.SPACING_48}` : 'padding-right: 0')};
    `};
    &:hover {
      background-color: ${({ $theme }) => $theme && $theme.hovered.background};
      ${badgeStyled.Badge} {
        ${({ $isCompactBadge, border }) => ($isCompactBadge && border) && css`
          border: 0.125rem solid ${props => props.$hoverBorderColor};
        `};

      }
    }
    &:focus {
      outline: none;
      box-shadow: inset ${SHADOW_SPREAD.SHADOW_SPREAD_0} ${SHADOW_SPREAD.SHADOW_SPREAD_0} ${SHADOW_SPREAD.SHADOW_SPREAD_0} ${SHADOW_SPREAD.SHADOW_SPREAD_2}  ${({ $theme }) => $theme && $theme.focused.border};
      background-color: ${({ $theme }) => $theme && $theme.focused.background};
      ${SideDrawerVerticalBar} { // active bar under the selected border
        ${({ active, selected }) => active && !selected && css`
          left: ${SPACING.SPACING_2};
          top: ${SPACING.SPACING_2};
          width: ${VERTICAL_BAR_FOCUS_WIDTH};
          min-width: ${VERTICAL_BAR_FOCUS_WIDTH};
          height: calc(100% - ${SPACING.SPACING_4});
        `};
      }
    }
    &:hover:focus {
      background-color: ${({ $theme }) => $theme && $theme.hovered.focused.background};
    }
    ${({ selected, $theme }) => selected && SELECTED_STYLE($theme)};
    ${({ selected }) => selected &&
    css`
      background-color:  ${({ $theme }) => $theme && $theme.selected.background};
      &:focus {
        background-color:  ${({ $theme }) => $theme && $theme.selected.focused.background};
      }
      &:hover {
        background-color:  ${({ $theme }) => $theme && $theme.selected.hovered.background};
      }
      &:hover:focus {
        background-color:  ${({ $theme }) => $theme && $theme.selected.hovered.background};
      }
    `},
  `,
  SideDrawerItemContent: styled.span`  
    position: relative;
    z-index: ${zIndex.SIDEDRAWER_CONTENT};
    display: flex;
    align-items: center;
    justify-content: flex-start;
    height: ${ITEM_HEIGHT};
    ${({ $isSlim, $groupItem }) => $isSlim && $groupItem && css`
      height: ${SLIM_ITEM_HEIGHT}; 
    `};
    ${({ $isAllSlim }) => $isAllSlim && css`
      height: ${SLIM_ITEM_HEIGHT}; 
    `};
    padding: 0 ${SPACING.SPACING_12} 0 0;
    ${({ $hasIcon }) => $hasIcon && css`
      padding: 0 ${SPACING.SPACING_12} 0 ${SPACING.SPACING_12};
      ${({ $isCompact }) => $isCompact && css`
        padding: 0 ${SPACING.SPACING_12} 0 ${SPACING.SPACING_8};
      `};
    `};
    ${({ $hasIcon }) => !$hasIcon && css`
      padding: 0 ${SPACING.SPACING_12} 0 ${SPACING.SPACING_16};
    `};
    ${({ $groupItem }) => $groupItem && css`
      padding: 0 ${SPACING.SPACING_12} 0 0;
    `};
    ${({ $groupItem, groupHeaderHasIcon }) => $groupItem && !groupHeaderHasIcon && css`
      padding: 0 ${SPACING.SPACING_12} 0 ${SPACING_28};
    `};
    ${({ RTL }) => RTL && css`
      padding: 0 0 0 ${SPACING.SPACING_12};
      ${({ $hasIcon, $isCompact }) => $hasIcon && !$isCompact && css`
        padding: 0 ${SPACING.SPACING_12} 0 ${SPACING.SPACING_12};
      `};
      ${({ $hasIcon, $isCompact }) => $hasIcon && $isCompact && css`
        padding: 0 ${SPACING.SPACING_8} 0 ${SPACING.SPACING_12};
      `};
      ${({ $hasIcon }) => !$hasIcon && css`
        padding: 0 ${SPACING.SPACING_16} 0 ${SPACING.SPACING_12};
     `};
      ${({ $groupItem }) => $groupItem && css`
        padding: 0 0 0 ${SPACING.SPACING_12};
      `};
      ${({ $groupItem, groupHeaderHasIcon }) => $groupItem && !groupHeaderHasIcon && css`
        padding: 0 ${SPACING_28} 0 ${SPACING.SPACING_12};
      `};
    `};
    width: 100%;
  `,
  IconWrapper: styled.span`
  ${({ RTL }) => (RTL ? `margin-left: ${SPACING.SPACING_4}` : ` margin-right: ${SPACING.SPACING_4}`)};
  ${({ $isCompact }) => $isCompact && css`
    ${({ RTL }) => (RTL ? `margin-left: ${SPACING.SPACING_12}` : ` margin-right: ${SPACING.SPACING_12}`)};
  `};
  `,
  LabelWrapper: styled.span`
    ${TYPOGRAPHY.BODY}
    color: ${({ $theme }) => $theme && $theme.sidedrawer.text};
    ${({ $isOpen }) => $isOpen && css`
      font-weight: ${FONT_WEIGHT.FONT_WEIGHT_BOLD}; 
    `};
    ${({ groupHeader }) => groupHeader &&
    css`
      color:  ${({ $theme }) => $theme && $theme.sidedrawer.groupHeading};
    `};
    ${({ selected, $theme }) => selected && SELECTED_LABEL_STYLE($theme)};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  BadgeWrapper: styled.span`
    ${({ RTL }) => (RTL ? 'margin-right: auto' : 'margin-left: auto')};
    display: flex;
    align-items: center;
  `,
  ExpandableIndicatorWrapper: styled.span`
    ${({ $hasBadge }) => !$hasBadge && css`
      ${({ RTL }) => (RTL ? 'margin-right: auto' : 'margin-left: auto')};
    `};
  `,
  SideDrawerItemDivider: styled.li`
    margin: 0;
    height: 0.0625rem;
    width: 100%;
    background-color: ${({ $theme }) => $theme && $theme.divider};
  `,
  SideDrawerButton: styled.span`
  position: relative;
  ${({ $RTL }) => ($RTL ? `margin-right: -${BUTTON_OFFSET}rem` : `margin-left: -${BUTTON_OFFSET}rem`)};
  ${badgeStyled.Badge} {
    box-sizing: content-box;
  }
  &:hover {
    ${badgeStyled.Badge} {
      border: 0.125rem solid ${props => props.$hoverBorderColor};
    }
  }
  `
};

